import { HISTORY_CODES } from 'dex-app.cm';

import { CONTRACT_PARAMS, supportedTokens } from '@/constants';
import { HistoryRoute } from '@/types';

import { HighlightColor, HISTORY_STATUS, historyErrorCodes, linkMessages } from './constants';
import { HistoryItem } from './types';

const usedHistoryIds = new Map<string, HighlightColor>();
const usedColors = new Set<HighlightColor>();

export const getRandomColor = (historyId: string): HighlightColor => {
  if (usedHistoryIds.has(historyId)) {
    return usedHistoryIds.get(historyId)!;
  }

  let colors = Object.values(HighlightColor).filter(
    (color) => !usedColors.has(color) && color !== HighlightColor.white
  );

  if (!colors.length) {
    colors = Object.values(HighlightColor);
    usedColors.clear();
  }

  let newColor = colors[Math.floor(Math.random() * colors.length)];

  if (newColor === HighlightColor.white) {
    newColor = getRandomColor(historyId);
  }

  usedHistoryIds.set(historyId, newColor);
  usedColors.add(newColor);

  return newColor;
};

export const getHistoryItemStatus = (historyItemCode: HISTORY_CODES) => {
  return (historyErrorCodes as HISTORY_CODES[]).includes(historyItemCode) ? HISTORY_STATUS.ERROR : HISTORY_STATUS.OK;
};

export const getHistoryScope = (code: HISTORY_CODES) => {
  const isOrderC1 = (code >= 300 && code < 320) || (code >= 600 && code < 620) || code === HISTORY_CODES.C1_ORDER;

  const isOrderC2 = (code >= 400 && code < 410) || (code >= 700 && code < 710) || code === HISTORY_CODES.C2_ORDER;

  const isL1Tx = (code >= 320 && code < 330) || code === HISTORY_CODES.L1_TX || code === HISTORY_CODES.C1_L1_TX_HANDLED;

  const isSwap =
    (code >= 410 && code < 430) ||
    (code >= 710 && code < 730) ||
    code === HISTORY_CODES.C1_L1_TRANSFER_TX_HANDLED ||
    code === HISTORY_CODES.TRANSFER;

  const isTake = code === HISTORY_CODES.C1_L1_TAKE_TX_HANDLED || code === HISTORY_CODES.TRANSFER;

  const isCollateral = (code >= 430 && code < 440) || (code >= 730 && code < 740) || code === HISTORY_CODES.COLLATERAL;
  const isBatch = [HISTORY_CODES.BATCH, HISTORY_CODES.BATCH_SUCCESS, HISTORY_CODES.BATCH_ERROR].includes(code);

  switch (true) {
    case isOrderC1:
      return linkMessages.orderC1;
    case isOrderC2:
      return linkMessages.orderC2;
    case isSwap:
      return linkMessages.swap;
    case isTake:
      return linkMessages.take;
    case isCollateral:
      return linkMessages.collateral;
    case isBatch:
      return linkMessages.batch;
    case isL1Tx:
      return linkMessages.l1Tx;
    default: {
      return linkMessages.unknown;
    }
  }
};

export const getContractIdsFromRoute = ({
  route,
  withAllDirections,
}: {
  route?: HistoryRoute;
  withAllDirections?: boolean;
}) => {
  if (!route) {
    return supportedTokens.flatMap((token) => [
      CONTRACT_PARAMS[token].L2_CONTRACT_ADDRESS_BASE,
      CONTRACT_PARAMS[token].L2_CONTRACT_ADDRESS_MAKER,
    ]);
  }

  const contractIds: string[] = [];

  if (route[0]) {
    contractIds.push(CONTRACT_PARAMS[route[0]].L2_CONTRACT_ADDRESS_BASE);

    if (withAllDirections) {
      contractIds.push(CONTRACT_PARAMS[route[0]].L2_CONTRACT_ADDRESS_MAKER);
    }
  }

  if (route[1]) {
    contractIds.push(CONTRACT_PARAMS[route[1]].L2_CONTRACT_ADDRESS_MAKER);

    if (withAllDirections) {
      contractIds.push(CONTRACT_PARAMS[route[1]].L2_CONTRACT_ADDRESS_BASE);
    }
  }

  return contractIds;
};

export const calculateItemOrder = (item: HistoryItem): number => {
  return item.timestamp + item.step * 1000 + item.order * 100;
};
